import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICES } from "@/lib/siteData";
import { RevealLines, FadeUp } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">{label}</span>
    <input
      {...props}
      className="mt-3 w-full bg-transparent border-b border-[var(--line)] py-3 text-lg text-[var(--off)] placeholder:text-[var(--off)]/25 focus:border-[var(--off)] focus:outline-none transition-colors duration-300"
    />
  </label>
);

export const Consult = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", case_type: SERVICES[0].title, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill your name, phone and a short message.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || undefined };
      await axios.post(`${API}/consultation`, payload);
      setDone(true);
      toast.success("Enquiry received. We will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="consult" data-testid="consult-section" className="px-6 md:px-12 py-24 md:py-40">
      <div className="mx-auto max-w-[1500px]">
        <FadeUp className="flex items-baseline gap-4 mb-16 md:mb-24 border-b border-[var(--line)] pb-6">
          <span className="font-mono-plex text-xs text-[var(--muted)]">05</span>
          <span className="font-mono-plex text-xs uppercase tracking-[0.28em] text-[var(--muted)]">Book a Consultation</span>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <RevealLines
              tag="h2"
              className="font-serif-display font-light text-4xl md:text-6xl tracking-tight leading-[0.92] mb-10"
              lines={["Let's discuss", "your matter."]}
            />
            <FadeUp delay={0.1}>
              <p className="text-base md:text-lg text-[var(--off)]/70 leading-relaxed mb-12 max-w-md">
                Share a few details and {CONTACT.name} will respond personally. For urgent matters, call or message directly.
              </p>
              <div className="space-y-4">
                <a href={`tel:${CONTACT.phone}`} data-testid="consult-call" className="group flex items-center justify-between border border-[var(--line)] px-6 py-5 hover:bg-[var(--off)] hover:text-[#060606] transition-colors duration-300">
                  <span className="flex items-center gap-3 font-mono-plex text-xs uppercase tracking-[0.18em]"><Phone size={15} /> Call</span>
                  <span className="font-serif-display text-xl">{CONTACT.phoneDisplay}</span>
                </a>
                <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="consult-whatsapp" className="group flex items-center justify-between border border-[var(--line)] px-6 py-5 hover:bg-[var(--off)] hover:text-[#060606] transition-colors duration-300">
                  <span className="flex items-center gap-3 font-mono-plex text-xs uppercase tracking-[0.18em]"><MessageCircle size={15} /> WhatsApp</span>
                  <span className="font-serif-display text-xl">Message Now</span>
                </a>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="consult-success"
                className="h-full min-h-[400px] flex flex-col items-center justify-center text-center border border-[var(--line)] p-10"
              >
                <div className="grid place-items-center h-16 w-16 rounded-full bg-[var(--off)] text-[#060606] mb-8">
                  <Check size={28} />
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl mb-4">Thank you.</h3>
                <p className="text-[var(--off)]/70 max-w-sm">Your enquiry has been received. {CONTACT.name} will reach out to you shortly.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="consult-form" className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                <Field label="Full Name" data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Your name" />
                <Field label="Phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="Contact number" />
                <Field label="Email (optional)" data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
                <label className="block">
                  <span className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">Case Type</span>
                  <select
                    data-testid="input-casetype"
                    value={form.case_type}
                    onChange={update("case_type")}
                    className="mt-3 w-full bg-transparent border-b border-[var(--line)] py-3 text-lg text-[var(--off)] focus:border-[var(--off)] focus:outline-none transition-colors duration-300"
                  >
                    {SERVICES.map((s) => (
                      <option key={s.title} value={s.title} className="bg-[#101010] text-[var(--off)]">{s.title}</option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">Brief Message</span>
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Describe your matter briefly..."
                    className="mt-3 w-full bg-transparent border-b border-[var(--line)] py-3 text-lg text-[var(--off)] placeholder:text-[var(--off)]/25 focus:border-[var(--off)] focus:outline-none transition-colors duration-300 resize-none"
                  />
                </label>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="consult-submit"
                    className="group w-full md:w-auto flex items-center justify-center gap-3 bg-[var(--off)] text-[#060606] px-10 py-5 font-mono-plex text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Sending</> : "Send Enquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
